// src/components/Engagement/Polls.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  getPolls,
  getPoll,
  createPoll,
  updatePoll,
  deletePoll,
  duplicatePoll,
  activatePoll,
  closePoll,
  getPollResponses,
  submitPollResponse,
  getPollStats,
  exportPolls,
  clearApiMessage,
  clearError,
  clearPollState,
  selectPolls,
  selectCurrentPoll,
  selectPollResponses,
  selectPollStats,
  selectPollPagination,
  selectPollLoading,
  selectPollError,
  selectPollApiMessage,
  selectPollSuccess,
  selectPollExportData,
  selectPollStatsSummary,
} from '../../store/slices/engagement/pollSlice';
import { 
  getMembers, 
  selectMembers,
  selectMemberPagination,
  selectAvailableGroups 
} from '../../store/slices/main/memberSlice';
import { 
  getVisitors, 
  selectVisitors,
  selectVisitorPagination 
} from '../../store/slices/growth/visitorSlice';
import {
  Download,
  BarChart3,
  FileJson,
  TrendingUp,
  FileText,
  Pencil,
  Trash2,
  AlertTriangle,
  Save,
  Rocket,
  Copy,
  CheckCircle2,
  MessageSquare,
  FileEdit,
  Search,
  List,
  ClipboardList,
  Square,
  LayoutGrid,
  Users,
  Users2,
  UserPlus,
  Loader2,
} from 'lucide-react';

const Polls = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingPollId, setEditingPollId] = useState(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [dynamicAudienceOptions, setDynamicAudienceOptions] = useState([]);

  // Refs for edit form
  const editQuestionRef = useRef(null);
  const editTypeRef = useRef(null);
  const editSendToRef = useRef(null);
  const editDateRef = useRef(null);
  const editRepeatRef = useRef(null);
  const editMessageRef = useRef(null);
  const editOptionsRef = useRef(null);

  // Refs for create form
  const createQuestionRef = useRef(null);
  const createTypeRef = useRef(null);
  const createSendToRef = useRef(null);
  const createDateRef = useRef(null);
  const createRepeatRef = useRef(null);
  const createMessageRef = useRef(null);
  const createOptionsRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const pollList = useSelector(selectPolls) || [];
  const currentPoll = useSelector(selectCurrentPoll);
  const pollResponses = useSelector(selectPollResponses) || [];
  const stats = useSelector(selectPollStatsSummary);
  const pagination = useSelector(selectPollPagination);
  const loading = useSelector(selectPollLoading);
  const apiMessage = useSelector(selectPollApiMessage);
  const error = useSelector(selectPollError);
  const successMessage = useSelector(selectPollSuccess);
  const exportData = useSelector(selectPollExportData);
  
  // MEMBER AND VISITOR SELECTORS
  const members = useSelector(selectMembers) || [];
  const memberPagination = useSelector(selectMemberPagination) || {};
  const visitors = useSelector(selectVisitors) || [];
  const visitorPagination = useSelector(selectVisitorPagination) || {};
  const groups = useSelector(selectAvailableGroups) || [];

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingOptions(true);
      try {
        await Promise.all([
          dispatch(getPolls({ 
            per_page: itemsPerPage, 
            page: currentPage,
            search: searchTerm,
            status: statusFilter
          })),
          dispatch(getPollStats()),
          dispatch(getMembers({ per_page: 50 })),
          dispatch(getVisitors({ per_page: 50 })),
        ]);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setIsLoadingOptions(false);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearPollState());
    };
  }, [dispatch]);

  // Build dynamic audience options
  useEffect(() => {
    buildDynamicAudienceOptions();
  }, [members, visitors, groups, memberPagination, visitorPagination]);

  // Handle filters and pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (statusFilter) params.status = statusFilter;
      
      dispatch(getPolls(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication (same as Members component)
  useEffect(() => {
    // Combine apiMessage and successMessage
    const message = apiMessage || successMessage;
    if (!message || !showToast || !shouldShowApiToast(message, !!error)) return;
    
    const messageKey = `${message}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(message, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, successMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== BUILD DYNAMIC AUDIENCE OPTIONS ====================
  
  const buildDynamicAudienceOptions = () => {
    const options = [];
    
    // 1. MEMBER OPTIONS
    const memberCount = memberPagination?.total || members.length || 0;
    const activeMemberCount = members.filter(m => m.status === 'Active').length;
    const inactiveMemberCount = members.filter(m => m.status === 'Inactive').length;
    const visitorCount = visitorPagination?.total || visitors.length || 0;
    
    options.push(
      { id: 'all_members', value: 'All Members', label: `🌐 All Members (${memberCount})`, type: 'members' },
      { id: 'active_members', value: 'Active Members', label: `✅ Active Members (${activeMemberCount})`, type: 'members' },
      { id: 'inactive_members', value: 'Inactive Members', label: `⭕ Inactive Members (${inactiveMemberCount})`, type: 'members' },
      { id: 'all_visitors', value: 'All Visitors', label: `👋 All Visitors (${visitorCount})`, type: 'visitors' }
    );
    
    // 2. MEMBER GROUPS
    let groupsList = [];
    
    // Try from member pagination
    if (memberPagination?.groups && Array.isArray(memberPagination.groups)) {
      groupsList = memberPagination.groups;
    } 
    // Try from groups selector
    else if (groups && groups.length > 0) {
      groupsList = groups;
    }
    // Extract from members data
    else if (members && members.length > 0) {
      const uniqueGroups = new Set();
      members.forEach(m => {
        if (m.group) uniqueGroups.add(m.group);
      });
      groupsList = Array.from(uniqueGroups);
    }
    
    // Add member groups to options
    groupsList.forEach(group => {
      const groupMemberCount = members.filter(m => m.group === group).length;
      options.push({
        id: `group_${group}`,
        value: group,
        label: `👥 ${group} (${groupMemberCount} members)`,
        type: 'group'
      });
    });
    
    // 3. VISITOR SOURCES
    const sources = new Set();
    visitors.forEach(v => {
      if (v.source) sources.add(v.source);
      if (v.referred_by) sources.add(v.referred_by);
      if (v.visitor_type) sources.add(v.visitor_type);
    });
    
    Array.from(sources).filter(Boolean).forEach(source => {
      const sourceCount = visitors.filter(v => 
        v.source === source || v.referred_by === source || v.visitor_type === source
      ).length;
      options.push({
        id: `source_${source}`,
        value: `Source: ${source}`,
        label: `📌 ${source} (${sourceCount} visitors)`,
        type: 'visitor_source'
      });
    });
    
    // 4. BRANCHES
    const branches = new Set();
    members.forEach(m => {
      if (m.branch) branches.add(m.branch);
    });
    visitors.forEach(v => {
      if (v.branch) branches.add(v.branch);
    });
    
    Array.from(branches).filter(Boolean).forEach(branchName => {
      const memberCount = members.filter(m => m.branch === branchName).length;
      const visitorCount = visitors.filter(v => v.branch === branchName).length;
      const total = memberCount + visitorCount;
      options.push({
        id: `branch_${branchName}`,
        value: `Branch: ${branchName}`,
        label: `🏛️ ${branchName} (${total} people)`,
        type: 'branch'
      });
    });
    
    // 5. RECENT/NEW OPTIONS
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const newMembers = members.filter(m => {
      const joinedDate = new Date(m.created_at || m.joined_date);
      return joinedDate >= thirtyDaysAgo;
    });
    if (newMembers.length > 0) {
      options.push({
        id: 'new_members',
        value: 'New Members',
        label: `🌟 New Members (${newMembers.length})`,
        type: 'members'
      });
    }
    
    const recentVisitors = visitors.filter(v => {
      const visitedDate = new Date(v.visited_at || v.created_at);
      return visitedDate >= thirtyDaysAgo;
    });
    if (recentVisitors.length > 0) {
      options.push({
        id: 'recent_visitors',
        value: 'Recent Visitors',
        label: `🔔 Recent Visitors (${recentVisitors.length})`,
        type: 'visitors'
      });
    }
    
    setDynamicAudienceOptions(options);
  };

  // ==================== HELPERS ====================

  // Filter polls locally for search (already handled by API)
  const filteredPolls = pollList;

  // Pagination calculations
  const totalItems = filteredPolls.length;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPolls.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // Get audience label for display
  const getAudienceLabel = (sendTo) => {
    if (!sendTo) return 'All Members';
    const option = dynamicAudienceOptions.find(opt => opt.value === sendTo);
    return option?.label || sendTo;
  };

  // ==================== EXPORT FUNCTIONS ====================

  const handleExport = async (type, pollId = null, format = 'csv') => {
    try {
      setIsSubmitting(true);
      const params = {};
      if (pollId) params.poll_id = pollId;
      
      const result = await dispatch(exportPolls({ type, params })).unwrap();
      console.log('Export result:', result);
      
      if (result.success && result.exportData) {
        let data = result.exportData;
        
        if (data.data && Array.isArray(data.data)) {
          data = data.data;
        } 
        else if (typeof data === 'object' && !Array.isArray(data)) {
          if (data.data && Array.isArray(data.data)) {
            data = data.data;
          } else {
            data = Object.values(data);
          }
        }
        
        if (!Array.isArray(data) || data.length === 0) {
          showToastMessage('No data to export', 'error');
          return;
        }
        
        const filename = getExportFilename(`polls-${type}`);
        
        switch (format.toLowerCase()) {
          case 'csv':
            exportToCSV(data, filename);
            break;
          case 'json':
            exportToJSON(data, filename);
            break;
          case 'excel':
            exportToExcel(data, filename);
            break;
          case 'pdf':
            exportToPDF(data, 'Polls Report', filename);
            break;
          default:
            showToastMessage('Unsupported export format', 'error');
            return;
        }
        
        showToastMessage(`📥 ${data.length} records exported as ${format.toUpperCase()}!`, 'success');
        closeModal();
      }
    } catch (err) {
      console.error('Export error:', err);
      showToastMessage(err.message || 'Failed to export data', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Export Modal
  const openExportModal = (exportType = 'all', poll = null) => {
    let data = [];
    let title = 'Polls Report';
    let filename = 'polls-report';

    if (exportType === 'single' && poll) {
      const resultsData = poll.results?.map(r => ({
        pollQuestion: poll.question,
        pollType: poll.type,
        pollStatus: poll.status,
        responses: poll.response_count || 0,
        sent: poll.sent_count || 0,
        responseRate: poll.response_rate || '0%',
        optionLabel: r.label,
        optionVotes: r.votes,
        optionPercentage: `${r.pct}%`,
        date: poll.formatted_date || poll.created_at
      })) || [];
      data = resultsData;
      title = `Poll Report: ${poll.question}`;
      filename = `poll-${poll.id}-${poll.question?.substring(0, 30)}`;
    } else if (exportType === 'summary') {
      data = pollList.map(p => ({
        'Poll ID': p.id,
        'Question': p.question,
        'Type': p.type,
        'Status': p.status,
        'Date': p.formatted_date || p.created_at,
        'Sent To': p.sent_count || 0,
        'Responses': p.response_count || 0,
        'Response Rate': p.response_rate || '0%',
        'Top Result': p.results?.length > 0 ? p.results.reduce((a, b) => a.votes > b.votes ? a : b).label : 'N/A'
      }));
      title = 'Polls Summary Report';
      filename = 'polls-summary';
    } else {
      data = filteredPolls.length > 0 ? filteredPolls : pollList;
      data = data.map(p => ({
        'Poll ID': p.id,
        'Question': p.question,
        'Type': p.type,
        'Status': p.status,
        'Date': p.formatted_date || p.created_at,
        'Sent To': p.sent_count || 0,
        'Responses': p.response_count || 0,
        'Response Rate': p.response_rate || '0%',
        'Results': p.results?.map(r => `${r.label}: ${r.votes} (${r.pct}%)`).join('; ') || 'N/A'
      }));
      title = 'All Polls Report';
      filename = 'all-polls';
    }

    if (data.length === 0) {
      showToastMessage('No data to export', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> Export {title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} record{data.length !== 1 ? 's' : ''} in your preferred format.
            {exportType === 'single' && poll && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · {poll.question}
              </span>
            )}
            {exportType === 'summary' && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · Summary of {pollList.length} polls
              </span>
            )}
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleExport(exportType, poll?.id, format)}
                  disabled={isSubmitting}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <FileJson size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-3px' }} /> {data.length} records will be exported</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // Export all polls
  const handleExportAll = () => {
    if (pollList.length === 0) {
      showToastMessage('No polls to export', 'error');
      return;
    }
    openExportModal('all');
  };

  // Export single poll
  const handleExportPoll = (poll) => {
    openExportModal('single', poll);
  };

  // Export summary
  const handleExportSummary = () => {
    if (pollList.length === 0) {
      showToastMessage('No polls to export', 'error');
      return;
    }
    openExportModal('summary');
  };

  // ==================== POLL CRUD ====================

  const openCreatePoll = () => {
    setTimeout(() => {
      if (createQuestionRef.current) createQuestionRef.current.value = '';
      if (createTypeRef.current) createTypeRef.current.value = 'Rating';
      if (createSendToRef.current) createSendToRef.current.value = 'All Members';
      if (createDateRef.current) createDateRef.current.value = '';
      if (createRepeatRef.current) createRepeatRef.current.value = 'One-time';
      if (createMessageRef.current) createMessageRef.current.value = '';
      if (createOptionsRef.current) createOptionsRef.current.value = '';
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Create New Poll</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Poll Question <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              ref={createQuestionRef}
              placeholder="How would you rate today's service?" 
            />
          </div>
          <div className="form-group">
            <label className="form-label">Poll Type <span style={{ color: 'var(--red)' }}>*</span></label>
            <select 
              className="form-select"
              ref={createTypeRef}
              defaultValue="Rating"
            >
              <option value="Rating">Rating (1–5)</option>
              <option value="Single Choice">Single Choice</option>
              <option value="Yes/No">Yes/No</option>
              <option value="Open-ended">Open-ended</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Options (comma separated)</label>
            <input 
              className="form-input" 
              ref={createOptionsRef}
              placeholder="Excellent, Good, Average, Poor"
            />
            <div className="form-hint">For Single Choice polls only. Leave empty for Rating or Yes/No.</div>
          </div>
          <div className="form-group">
            <label className="form-label">Send To</label>
            <select 
              className="form-select"
              ref={createSendToRef}
              defaultValue="All Members"
            >
              {isLoadingOptions ? (
                <option value="" disabled>Loading audiences...</option>
              ) : dynamicAudienceOptions.length > 0 ? (
                dynamicAudienceOptions.map(option => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))
              ) : (
                <option value="" disabled>No audiences available</option>
              )}
            </select>
            <div className="form-hint" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span><Users size={14} style={{ verticalAlign: '-3px' }} /> {members.length} members loaded</span>
              <span><UserPlus size={14} style={{ verticalAlign: '-3px' }} /> {visitors.length} visitors loaded</span>
              <span><BarChart3 size={14} style={{ verticalAlign: '-3px' }} /> {dynamicAudienceOptions.length} audience options</span>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Send Date & Time</label>
              <input 
                className="form-input" 
                type="datetime-local"
                ref={createDateRef}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Repeat</label>
              <select 
                className="form-select"
                ref={createRepeatRef}
                defaultValue="One-time"
              >
                <option value="One-time">One-time</option>
                <option value="Every Sunday">Every Sunday</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Intro SMS Text</label>
            <textarea 
              className="form-textarea" 
              rows="3"
              ref={createMessageRef}
              placeholder="Hello {FirstName}! We'd love your feedback on today's service. Reply with a number from 1 (Poor) to 5 (Excellent)."
            />
            <div className="form-hint">
              Use {'{FirstName}'} for personalisation
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const question = createQuestionRef.current?.value?.trim() || '';
              const type = createTypeRef.current?.value || 'Rating';
              const sendTo = createSendToRef.current?.value || 'All Members';
              const sendDate = createDateRef.current?.value || null;
              const repeat = createRepeatRef.current?.value || 'One-time';
              const introMessage = createMessageRef.current?.value || '';
              const optionsRaw = createOptionsRef.current?.value || '';
              const options = optionsRaw ? optionsRaw.split(',').map(o => o.trim()).filter(o => o) : null;

              if (!question) {
                showToastMessage('Please enter a poll question', 'error');
                return;
              }

              setIsSubmitting(true);
              try {
                const result = await dispatch(createPoll({
                  question,
                  type,
                  send_to: sendTo,
                  send_date: sendDate,
                  repeat,
                  intro_message: introMessage,
                  options,
                  status: 'Draft'
                })).unwrap();

                if (result.success) {
                  closeModal();
                  await dispatch(getPolls({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getPollStats());
                  showToastMessage('📊 Poll created successfully!', 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to create poll', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Poll'}
          </button>
        </div>
      </div>
    );
  };

  const handleEditPoll = (poll) => {
    setEditingPollId(poll.id);
    
    setTimeout(() => {
      if (editQuestionRef.current) editQuestionRef.current.value = poll.question || '';
      if (editTypeRef.current) editTypeRef.current.value = poll.type || 'Rating';
      if (editSendToRef.current) editSendToRef.current.value = poll.send_to || 'All Members';
      if (editDateRef.current) {
        if (poll.send_date) {
          const date = new Date(poll.send_date);
          if (!isNaN(date.getTime())) {
            editDateRef.current.value = date.toISOString().slice(0, 16);
          }
        }
      }
      if (editRepeatRef.current) editRepeatRef.current.value = poll.repeat || 'One-time';
      if (editMessageRef.current) editMessageRef.current.value = poll.intro_message || '';
      if (editOptionsRef.current) editOptionsRef.current.value = poll.options?.join(', ') || '';
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Poll</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Poll Question <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              ref={editQuestionRef}
              defaultValue={poll.question || ''}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Poll Type</label>
            <select 
              className="form-select"
              ref={editTypeRef}
              defaultValue={poll.type || 'Rating'}
            >
              <option value="Rating">Rating (1–5)</option>
              <option value="Single Choice">Single Choice</option>
              <option value="Yes/No">Yes/No</option>
              <option value="Open-ended">Open-ended</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Options (comma separated)</label>
            <input 
              className="form-input" 
              ref={editOptionsRef}
              defaultValue={poll.options?.join(', ') || ''}
            />
            <div className="form-hint">For Single Choice polls only</div>
          </div>
          <div className="form-group">
            <label className="form-label">Send To</label>
            <select 
              className="form-select"
              ref={editSendToRef}
              defaultValue={poll.send_to || 'All Members'}
            >
              {isLoadingOptions ? (
                <option value="" disabled>Loading audiences...</option>
              ) : dynamicAudienceOptions.length > 0 ? (
                dynamicAudienceOptions.map(option => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))
              ) : (
                <option value="" disabled>No audiences available</option>
              )}
            </select>
            <div className="form-hint" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span><Users size={14} style={{ verticalAlign: '-3px' }} /> {members.length} members loaded</span>
              <span><UserPlus size={14} style={{ verticalAlign: '-3px' }} /> {visitors.length} visitors loaded</span>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Send Date & Time</label>
              <input 
                className="form-input" 
                type="datetime-local"
                ref={editDateRef}
                defaultValue=""
              />
            </div>
            <div className="form-group">
              <label className="form-label">Repeat</label>
              <select 
                className="form-select"
                ref={editRepeatRef}
                defaultValue={poll.repeat || 'One-time'}
              >
                <option value="One-time">One-time</option>
                <option value="Every Sunday">Every Sunday</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Intro SMS Text</label>
            <textarea 
              className="form-textarea" 
              rows="3"
              ref={editMessageRef}
              defaultValue={poll.intro_message || ''}
            />
            <div className="form-hint">
              Use {'{FirstName}'} for personalisation
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const question = editQuestionRef.current?.value?.trim() || '';
              const type = editTypeRef.current?.value || poll.type;
              const sendTo = editSendToRef.current?.value || poll.send_to;
              const sendDate = editDateRef.current?.value || poll.send_date;
              const repeat = editRepeatRef.current?.value || poll.repeat;
              const introMessage = editMessageRef.current?.value || '';
              const optionsRaw = editOptionsRef.current?.value || '';
              const options = optionsRaw ? optionsRaw.split(',').map(o => o.trim()).filter(o => o) : null;

              if (!question) {
                showToastMessage('Please enter a poll question', 'error');
                return;
              }

              setIsSubmitting(true);
              try {
                const result = await dispatch(updatePoll({
                  id: poll.id,
                  data: {
                    question,
                    type,
                    send_to: sendTo,
                    send_date: sendDate,
                    repeat,
                    intro_message: introMessage,
                    options
                  }
                })).unwrap();

                if (result.success) {
                  closeModal();
                  await dispatch(getPolls({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getPollStats());
                  showToastMessage(`✅ Poll updated: "${question}"`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to update poll', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Saving...' : <><Save size={14} style={{ verticalAlign: '-2px' }} /> Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  const handleDeletePoll = (poll) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Poll</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{poll.question}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will permanently remove the poll and all responses.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deletePoll(poll.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getPolls({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getPollStats());
                  showToastMessage(`🗑️ Poll deleted: "${poll.question}"`, 'error');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete poll', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Delete Poll
          </button>
        </div>
      </div>
    );
  };

  const handleStopPoll = async (poll) => {
    try {
      setIsSubmitting(true);
      const result = await dispatch(closePoll(poll.id)).unwrap();
      if (result.success) {
        await dispatch(getPolls({ per_page: itemsPerPage, page: currentPage }));
        await dispatch(getPollStats());
        showToastMessage(`⏹️ Poll "${poll.question}" closed`, 'info');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to close poll', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicatePoll = async (poll) => {
    try {
      setIsSubmitting(true);
      const result = await dispatch(duplicatePoll(poll.id)).unwrap();
      if (result.success) {
        await dispatch(getPolls({ per_page: itemsPerPage, page: currentPage }));
        await dispatch(getPollStats());
        showToastMessage(`📋 Poll duplicated: "${result.poll?.question || poll.question}"`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to duplicate poll', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivatePoll = async (poll) => {
    try {
      setIsSubmitting(true);
      const result = await dispatch(activatePoll(poll.id)).unwrap();
      if (result.success) {
        await dispatch(getPolls({ per_page: itemsPerPage, page: currentPage }));
        await dispatch(getPollStats());
        showToastMessage(`✅ Poll "${poll.question}" activated!`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to activate poll', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== RENDER ====================

  const statsData = stats || {
    totalPolls: 0,
    activePolls: 0,
    closedPolls: 0,
    draftPolls: 0,
    totalResponses: 0,
    totalSent: 0,
    avgResponseRate: 0,
    pollsByType: {},
    responsesByMonth: []
  };

  // Render grid view
  const renderGridView = () => {
    return (
      <div className="grid-2">
        {currentItems.map(p => {
          const rate = p.response_count && p.sent_count ? Math.round((p.response_count / p.sent_count) * 100) : 0;
          const audienceLabel = getAudienceLabel(p.send_to);
          return (
            <div key={p.id} className="card">
              <div className="card-header">
                <div>
                  <div className="card-title" style={{ fontSize: '14px' }}>{p.question}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
                    {p.formatted_date || p.created_at} · {p.type} · 
                    <span className={`badge ${p.status === 'Active' ? 'badge-green' : p.status === 'Closed' ? 'badge-navy' : 'badge-amber'}`} style={{ fontSize: '10px', marginLeft: '4px' }}>
                      {p.status || 'Draft'}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '2px' }}>
                    Audience: <strong>{audienceLabel}</strong>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button 
                    className="btn btn-ghost btn-sm btn-icon" 
                    onClick={() => handleExportPoll(p)}
                    title={`Export report for "${p.question}"`}
                  >
                    <Download size={14} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleEditPoll(p)}
                    title={`Edit poll: ${p.question}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleDeletePoll(p)}
                    title={`Delete poll: ${p.question}`}
                    style={{ color: 'var(--red)' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px', marginBottom: '14px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{p.response_count || 0}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>responses</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{p.sent_count || 0}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>sent</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: rate > 70 ? 'var(--green)' : 'var(--amber)' }}>
                    {rate}%
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>rate</div>
                </div>
              </div>

              {p.results && p.results.map(r => (
                <div key={r.label} className="poll-option">
                  <div className="poll-option-label">{r.label}</div>
                  <div className="poll-bar-wrap">
                    <div className="poll-bar-fill" style={{ width: `${r.pct}%`, background: r.color || '#0891b2' }}></div>
                  </div>
                  <div className="poll-pct">{r.pct}%</div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                {p.status === 'Draft' && (
                  <button 
                    className="btn btn-gold btn-sm" 
                    onClick={() => handleActivatePoll(p)}
                    title={`Activate poll: "${p.question}"`}
                    disabled={isSubmitting}
                  >
                    <Rocket size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Activate
                  </button>
                )}
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleExportPoll(p)}
                  title={`Export report for "${p.question}"`}
                >
                  <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export Report
                </button>
                {p.status === 'Active' && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleStopPoll(p)}
                    title={`Stop poll: "${p.question}"`}
                  >
                    <Square size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Stop Poll
                  </button>
                )}
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleDuplicatePoll(p)}
                  title={`Duplicate poll: "${p.question}"`}
                  disabled={isSubmitting}
                >
                  <Copy size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Copy
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render list view
  const renderListView = () => {
    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Question</th>
                <th>Type</th>
                <th>Status</th>
                <th>Audience</th>
                <th>Sent</th>
                <th>Responses</th>
                <th>Rate</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map(p => {
                const rate = p.response_count && p.sent_count ? Math.round((p.response_count / p.sent_count) * 100) : 0;
                const audienceLabel = getAudienceLabel(p.send_to);
                return (
                  <tr key={p.id}>
                    <td>
                      <div style={{ fontWeight: '500', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.question}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{p.formatted_date || p.created_at}</div>
                    </td>
                    <td><span className="badge badge-navy" style={{ fontSize: '10px' }}>{p.type}</span></td>
                    <td>
                      <span className={`badge ${p.status === 'Active' ? 'badge-green' : p.status === 'Closed' ? 'badge-navy' : 'badge-amber'}`} style={{ fontSize: '10px' }}>
                        {p.status || 'Draft'}
                      </span>
                    </td>
                    <td style={{ fontSize: '11px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {audienceLabel}
                    </td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}>{p.sent_count || 0}</td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}>{p.response_count || 0}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div className="progress-bar" style={{ width: '50px' }}>
                          <div className="progress-fill" style={{ width: `${rate}%`, background: rate > 70 ? 'var(--green)' : 'var(--amber)' }}></div>
                        </div>
                        <span style={{ fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', color: rate > 70 ? 'var(--green)' : 'var(--amber)' }}>{rate}%</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleExportPoll(p)}
                          title="Export"
                        >
                          <Download size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleEditPoll(p)}
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        {p.status === 'Draft' && (
                          <button
                            className="btn btn-gold btn-sm btn-icon"
                            onClick={() => handleActivatePoll(p)}
                            title="Activate"
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                          >
                            <Rocket size={14} />
                          </button>
                        )}
                        {p.status === 'Active' && (
                          <button
                            className="btn btn-danger btn-sm btn-icon"
                            onClick={() => handleStopPoll(p)}
                            title="Stop"
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                          >
                            <Square size={14} />
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleDuplicatePoll(p)}
                          title="Duplicate"
                          disabled={isSubmitting}
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleDeletePoll(p)}
                          title="Delete"
                          style={{ color: 'var(--red)' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <BarChart3 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Collect feedback from your congregation via SMS — no app or internet needed.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportSummary}
            title="Export summary of all polls"
            disabled={isSubmitting}
          >
            <ClipboardList size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export Summary
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportAll}
            title="Export all polls"
            disabled={isSubmitting}
          >
            <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export All
          </button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={openCreatePoll} 
            title="Create a new poll"
            disabled={isSubmitting}
          >
            + Create Poll
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card blue" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Polls</div>
          <div className="stat-value">{statsData.totalPolls}</div>
          <div className="stat-change up">↑ {statsData.activePolls} active</div>
          <div className="stat-icon"><BarChart3 size={20} /></div>
        </div>
        <div className="stat-card green" style={{ cursor: 'default' }}>
          <div className="stat-label">Avg Response Rate</div>
          <div className="stat-value">{statsData.avgResponseRate}%</div>
          <div className="stat-change up">↑ {statsData.avgResponseRate > 50 ? 'good engagement' : 'needs improvement'}</div>
          <div className="stat-icon"><CheckCircle2 size={20} /></div>
        </div>
        <div className="stat-card gold" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Responses</div>
          <div className="stat-value">{statsData.totalResponses}</div>
          <div className="stat-change up">↑ all time</div>
          <div className="stat-icon"><MessageSquare size={20} /></div>
        </div>
        <div className="stat-card purple" style={{ cursor: 'default' }}>
          <div className="stat-label">Draft Polls</div>
          <div className="stat-value">{statsData.draftPolls}</div>
          <div className="stat-change up">→ ready to publish</div>
          <div className="stat-icon"><FileEdit size={20} /></div>
        </div>
      </div>

      {/* Filters and View Toggle */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={16} /></span>
            <input 
              type="text" 
              placeholder="Search polls..." 
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search polls by question"
            />
          </div>
          <select 
            className="form-select" 
            style={{ width: '140px' }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            title="Filter by status"
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Closed">Closed</option>
            <option value="Draft">Draft</option>
          </select>
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            <button 
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'} btn-sm`} 
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <LayoutGrid size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Grid
            </button>
            <button
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <List size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />List
            </button>
          </div>
        </div>
      </div>

      {/* Polls Content */}
      {loading && pollList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading polls...</div>
        </div>
      ) : currentItems.length > 0 ? (
        <>
          {viewMode === 'grid' ? renderGridView() : renderListView()}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px',
              marginTop: '20px',
              padding: '16px',
              background: 'var(--white)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              boxShadow: 'var(--shadow)'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, totalItems)}</strong> of <strong>{totalItems}</strong> polls
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                  <select 
                    className="form-select" 
                    style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    title="Items per page"
                    disabled={loading}
                  >
                    <option value={3}>3</option>
                    <option value={6}>6</option>
                    <option value={9}>9</option>
                    <option value={12}>12</option>
                    <option value={24}>24</option>
                  </select>
                </div>
                
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1 || loading}
                    style={{ 
                      opacity: currentPage === 1 ? 0.5 : 1, 
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer' 
                    }}
                    title="Previous page"
                  >
                    ← Prev
                  </button>
                  
                  {getPageNumbers().map(page => (
                    <button 
                      key={page}
                      className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => goToPage(page)}
                      title={`Go to page ${page}`}
                      disabled={loading}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages || loading}
                    style={{ 
                      opacity: currentPage === totalPages ? 0.5 : 1, 
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' 
                    }}
                    title="Next page"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <div className="empty-icon"><BarChart3 size={32} strokeWidth={1.5} /></div>
          <div className="empty-title">No polls found</div>
          <div className="empty-text">Try adjusting your search or filters</div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .poll-option {
            flex-wrap: wrap;
          }
          .poll-option-label {
            min-width: 100%;
            margin-bottom: 4px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
          .table-wrap {
            overflow-x: auto;
          }
          .table-wrap table {
            min-width: 700px;
          }
        }
        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .poll-option {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 0;
        }
        .poll-option-label {
          min-width: 80px;
          font-size: 12px;
          color: var(--text-mid);
        }
        .poll-bar-wrap {
          flex: 1;
          height: 6px;
          background: var(--border);
          border-radius: 3px;
          overflow: hidden;
        }
        .poll-bar-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.6s ease;
        }
        .poll-pct {
          min-width: 40px;
          font-size: 12px;
          font-weight: 600;
          font-family: 'JetBrains Mono', monospace;
          color: var(--navy);
          text-align: right;
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-green {
          background: var(--green);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-amber {
          background: var(--amber);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-red {
          background: var(--red);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .progress-bar {
          height: 6px;
          background: var(--border);
          border-radius: 3px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.6s ease;
        }
      `}</style>
    </>
  );
};

export default Polls;